import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, GraduationCap, Info, Loader2, X } from 'lucide-react';
import { annoncerErreur, annoncerSucces, EtatErreur, SqueletteListe } from '../etats';

/* ═══════════════════════════════════════════════════════════════════════
   LA FORMATION D'UNE PERSONNE — on coche des domaines, elle suit leurs modules

   Fichier IDENTIQUE dans Front-end/admin (le coach) et Front-end/user (le
   dirigeant) : seuls les appels au serveur diffèrent, passés en `api`.

   On forme par domaine : la personne suit tous les modules du domaine,
   ceux publiés plus tard compris. Dans une formation, seuls les modules que
   l'organisation a retenus. Décocher retire le domaine sans effacer
   l'avancement.
   ═══════════════════════════════════════════════════════════════════════ */

/* `api` : { perimetre() → [{ id, nom, modules: [...] }], lire(participantId),
   ecrire(participantId, domaineIds) }, chacune rendant les données ou levant
   une erreur lisible. */
export default function DomainesParticipant({ participant, api, onClose, onSaved }) {
  const [domaines, setDomaines] = useState(null);
  const [actuels, setActuels] = useState([]);
  const [suivis, setSuivis] = useState({});
  const [coches, setCoches] = useState(new Set());
  const [erreur, setErreur] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  const charger = () => {
    setErreur(null);
    Promise.all([api.perimetre(), api.lire(participant.id)])
      .then(([perimetre, etat]) => {
        setDomaines((perimetre || []).filter((d) => d.id));
        setActuels(etat?.domaines || []);
        setCoches(new Set(etat?.domaines || []));
        const parModule = {};
        (etat?.modules || []).forEach((m) => { parModule[m.id] = m; });
        setSuivis(parModule);
      })
      .catch((e) => setErreur(e));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(charger, [participant.id]);

  useEffect(() => {
    const touche = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', touche);
    return () => document.removeEventListener('keydown', touche);
  }, [onClose]);

  const basculer = (id) => setCoches((prev) => {
    const suivant = new Set(prev);
    if (suivant.has(id)) suivant.delete(id); else suivant.add(id);
    return suivant;
  });

  const modifie = useMemo(
    () => actuels.length !== coches.size || actuels.some((id) => !coches.has(id)),
    [actuels, coches]);

  const enregistrer = async () => {
    setEnvoi(true);
    try {
      const ids = (domaines || []).map((d) => d.id).filter((id) => coches.has(id));
      const r = await api.ecrire(participant.id, ids);
      annoncerSucces(r.ajoutes
        ? `${participant.nom_affichage} est prévenu(e) : ${r.ajoutes} domaine${r.ajoutes > 1 ? 's' : ''} ouvert${r.ajoutes > 1 ? 's' : ''}.`
        : 'Formation enregistrée.');
      onSaved?.(r.modules_resume);
      onClose();
    } catch (e) {
      annoncerErreur(e.message || 'La formation n’a pas pu être enregistrée.');
    } finally {
      setEnvoi(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
         role="dialog" aria-modal="true" aria-label={`Formation de ${participant.nom_affichage}`}
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-[var(--bg-card)] rounded-cadre w-full max-w-xl shadow-carte flex flex-col max-h-[90vh]">
        <div className="flex items-start justify-between gap-3 p-6 pb-4">
          <div>
            <h3 className="text-xl font-semibold text-[var(--text-bold)]">Domaines de formation</h3>
            <p className="text-xs text-[var(--text-muted)] mt-1">
              {participant.nom_affichage} suivra tous les modules des domaines cochés.
            </p>
          </div>
          <button type="button" onClick={onClose} className="act act-icone act-tertiaire" aria-label="Fermer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="px-6 flex gap-2 text-xs text-[var(--text-muted)] leading-relaxed">
          <Info className="w-4 h-4 shrink-0 mt-px" />
          Un domaine regroupe plusieurs modules de l’Académie. Une attestation est délivrée pour
          chaque domaine dont tous les modules sont terminés.
        </p>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-3">
          {erreur ? (
            <EtatErreur erreur={erreur} onReessayer={charger} compact />
          ) : domaines === null ? (
            <SqueletteListe lignes={4} />
          ) : domaines.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)] py-6 text-center">
              Aucun module n’est encore à suivre dans ce projet.
            </p>
          ) : (
            <ul className="divide-y divide-[var(--border)]">
              {domaines.map((d) => {
                const faits = d.modules.filter((m) => suivis[m.id]?.statut === 'termine').length;
                return (
                  <li key={d.id}>
                    <label className="flex items-start gap-3 py-3 cursor-pointer">
                      <input type="checkbox" checked={coches.has(d.id)} onChange={() => basculer(d.id)}
                             className="mt-1 w-4 h-4 accent-[var(--accent)]" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-[var(--text-bold)]">{d.nom}</span>
                        <span className="block text-[11.5px] text-[var(--text-muted)]">
                          {d.modules.length} module{d.modules.length > 1 ? 's' : ''} : {d.modules.map((m) => m.titre).join(', ')}
                        </span>
                      </span>
                      {actuels.includes(d.id) && (
                        <span className="shrink-0 text-[11px] font-semibold text-[var(--text-muted)] inline-flex items-center gap-1">
                          {faits === d.modules.length && <CheckCircle2 className="w-3.5 h-3.5 text-[var(--accent-texte)]" />}
                          {faits} / {d.modules.length} terminé{faits > 1 ? 's' : ''}
                        </span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center gap-3 p-6 pt-4 border-t border-[var(--border)]">
          <span className="text-xs text-[var(--text-muted)] mr-auto">
            {coches.size} domaine{coches.size > 1 ? 's' : ''} coché{coches.size > 1 ? 's' : ''}
          </span>
          <button type="button" onClick={onClose} className="act act-contour">Annuler</button>
          <button type="button" onClick={enregistrer} disabled={envoi || !modifie || !domaines}
                  aria-busy={envoi || undefined} className="act">
            {envoi ? <Loader2 className="w-4 h-4 animate-spin" /> : <GraduationCap className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
